import { createAction, PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import DiseaseApi from 'api/DiseaseApi';
import LogApi from 'api/LogApi';
import { showNotification } from 'app-redux/notification.saga';
import * as dto from 'client/dto';

import { updateEventsLayerData } from 'map/map.saga';

import { fetchAllTemporal } from 'diseaseActivitiesTemporal/diseaseActivitiesTemporal.saga';
import diseaseActivitiesTimeFrameSelectors from 'diseaseActivitiesTimeFrame/diseaseActivitiesTimeFrame.selectors';
import { userLocationIdParam2AoiPayload } from 'diseaseActivityPanels/disacPanelUtils';
import { closePanel } from 'diseaseActivityPanels/diseaseActivityPanels.saga';
import diseaseActivityPanelsSelectors from 'diseaseActivityPanels/diseaseActivityPanels.selectors';
import {
  DiseaseActivityParams,
  PanelType,
} from 'diseaseActivityPanels/diseaseActivityPanels.slice';

import diseaseActivitiesInLocationSelectors from './diseaseActivitiesInLocation.selectors';
import {
  clearNestedCaseCounts,
  loadAllDiseaseRisksError,
  loadAllDiseaseRisksStart,
  loadAllDiseaseRisksSuccess,
  loadNestedCaseCountsSuccess,
} from './diseaseActivitiesInLocation.slice';

interface FetchDiseaseCaseCountsPayload {
  geonameId: number;
  startDate: string;
  diseaseId: number;
}
export const fetchDiseaseRisks = createAction('diseaseActivitiesInLocation/fetchDiseaseRisks');
export const fetchNestedCaseCount = createAction<FetchDiseaseCaseCountsPayload>(
  'diseaseActivitiesInLocation/fetchNestedCaseCount'
);
export const fetchAllCaseCounts = createAction('diseaseActivitiesInLocation/fetchAllCaseCounts');

export function* fetchDiseaseRisksByLocation() {
  try {
    const startDate: string = yield select(diseaseActivitiesTimeFrameSelectors.getStartDate);
    const disacParams: DiseaseActivityParams = yield select(
      diseaseActivityPanelsSelectors.disacParams
    );
    const { userLocationId } = disacParams;
    const geonameIdPayload = userLocationIdParam2AoiPayload(userLocationId);

    yield put(loadAllDiseaseRisksStart());
    yield put(updateEventsLayerData([]));
    const response: AxiosResponse<dto.RiskAggregationModel> = yield call(
      DiseaseApi.getDiseaseRiskByLocation,
      {
        geonameId: geonameIdPayload,
        caseCountStartDate: startDate,
      }
    );
    yield put(loadAllDiseaseRisksSuccess(response.data.diseaseRisks));

    const isSelectedDiseaseInvalid: boolean = yield select(
      diseaseActivitiesInLocationSelectors.isSelectedDiseaseInvalid
    );
    if (isSelectedDiseaseInvalid) {
      yield put(
        showNotification({
          type: 'SHOW_NEUTRAL_NOTIFICATION',
          payload: `Selected disease is not present in the current list`,
        })
      );
      yield put(closePanel(PanelType.DiseaseActivityListPanel));
    }
    yield put(fetchAllTemporal());
    yield put(updateEventsLayerData(response.data.countryPins));
    yield put(fetchAllCaseCounts());
  } catch (e) {
    yield put(loadAllDiseaseRisksError());
  }
}

export function* fetchAllCaseCountsSaga() {
  const startDate: string = yield select(diseaseActivitiesTimeFrameSelectors.getStartDate);
  const disacParams: DiseaseActivityParams = yield select(
    diseaseActivityPanelsSelectors.disacParams
  );
  const { userLocationId } = disacParams;
  const geonameIdPayload = userLocationIdParam2AoiPayload(userLocationId);
  const allDiseaseIds: number[] = yield select(
    diseaseActivitiesInLocationSelectors.getAllDiseaseIds
  );

  yield put(clearNestedCaseCounts());
  for (const diseaseId of allDiseaseIds) {
    yield put(
      fetchNestedCaseCount({
        geonameId: geonameIdPayload,
        startDate: startDate,
        diseaseId: diseaseId,
      })
    );
  }
}

export function* fetchNestedCaseCountSaga(action: PayloadAction<FetchDiseaseCaseCountsPayload>) {
  try {
    const response: AxiosResponse<dto.HierarchicalCaseCountModel> = yield call(
      DiseaseApi.getDiseaseCaseCount,
      {
        diseaseId: action.payload.diseaseId,
        locationId: action.payload.geonameId,
        caseCountStartDate: action.payload.startDate,
      }
    );
    yield put(
      loadNestedCaseCountsSuccess({
        diseaseId: action.payload.diseaseId,
        nestedCaseCounts: response.data,
      })
    );
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
  }
}

function* diseaseActivitiesInLocationSaga() {
  yield takeLatest(fetchDiseaseRisks, fetchDiseaseRisksByLocation);
  yield takeEvery(fetchNestedCaseCount, fetchNestedCaseCountSaga);
  yield takeEvery(fetchAllCaseCounts, fetchAllCaseCountsSaga);
}

export default diseaseActivitiesInLocationSaga;
